/**
 * How live events change a card's "agent at work" (t-gdi2Cd), so an open
 * board starts and stops the shimmer without a refetch.
 *
 * - A move by an agent (the event names it) starts it, in the column the
 *   ticket moved into, active now.
 * - A move by anyone else stops it: somebody took the ticket from the agent,
 *   and the card must not light up again if it comes back to that column.
 * - A comment by the agent at work keeps it going. Matched by name, as the
 *   server names agents, so an extension token's comment (also an
 *   `mcp_client`) or another agent's does not. An agent shown only as "AI"
 *   cannot be told from the extension or another unnamed agent by name, so
 *   no comment extends it here; the next fetch does.
 *
 * The server's list is the authority on the next fetch; this only keeps an
 * open board honest in between.
 */
import { UNNAMED_AGENT, workingAgentName, type Comment, type TicketSummary } from '@vd/types';

export function agentAfterMove(
  move: { toColumnId: string; agent?: string },
  nowIso: string,
): TicketSummary['agentWorking'] {
  return move.agent ? { client: move.agent, columnId: move.toColumnId, lastActiveAt: nowIso } : undefined;
}

export function agentAfterComment(ticket: TicketSummary, comment: Pick<Comment, 'authorType' | 'authorTokenLabel' | 'createdAt'>): TicketSummary['agentWorking'] {
  const agent = ticket.agentWorking;
  if (!agent || comment.authorType !== 'mcp_client' || agent.client === UNNAMED_AGENT) return agent;
  if (workingAgentName(comment.authorTokenLabel) !== agent.client) return agent;
  return { ...agent, lastActiveAt: comment.createdAt };
}
